import { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import {
  getAllLiveClasses,
  joinLiveClass,
  endLiveClass,
  deleteLiveClassroom,
  updateRecordingsForCompletedLiveClasses,
  updateLiveClass,
} from "../../../../../utils/liveClasses";
import { openLinkInNewTab } from "../../../../StudentPages/LiveClasses/utils";

const useLiveClassData = (id) => {
  const [loading, setLoading] = useState(false);

  // LiveClass data state
  const [liveClassData, setLiveClassData] = useState([]);
  const [upcomingClassData, setUpcomingClassData] = useState([]);
  const [completedClassData, setCompletedClassData] = useState([]);

  // Pagination states
  const [pageSize] = useState(10);
  const [upComingLoader, setUpCommingLoader] = useState(false);
  const [noOfUpcomingPages, setNoOfUpcomingPages] = useState(0);
  const [pageOfUpcoming, setPageOfUpcoming] = useState(1);
  const [liveLoader, setLiveLoader] = useState(false);
  const [noOfLivePages, setNoOfLivePages] = useState(0);
  const [pageOfLive, setPageOfLive] = useState(1);
  const [completedLoader, setCompletedLoader] = useState(false);
  const [noOfCompletedPages, setNoOfCompletedPages] = useState(0);
  const [pageOfCompleted, setPageOfCompleted] = useState(1);

  // Refresh mechanism
  const stopFirstRefresh = useRef(false);
  const stopFirstCompletedRefresh = useRef(false);
  const [refreshClasses, setRefreshClasses] = useState(false);
  const [refreshCompletedLiveClasses, setRefreshCompletedLiveClasses] =
    useState(false);
  const [refreshCompletedClasses, setRefreshCompletedClasses] = useState(false);
  const [completedCount, setCompletedCount] = useState(0);
  const [upcomingCount, setUpcomingCount] = useState(0);
  const [appliedFilters, setAppliedFilters] = useState({
    status: [],
    teachers: [],
    subjects: [],
  });

  // Get upcoming live classes
  const getUpomingLiveClasses = async ({
    pageNo,
    isConcate = false,
    isPageSize = false,
    updatedPageSize = 10,
    noLoading = false,
  }) => {
    try {
      if (!noLoading) {
        setUpCommingLoader(true);
      }

      const response = await getAllLiveClasses({
        id,
        pageNumber: pageNo - 1,
        pageSize: isPageSize ? updatedPageSize : pageSize,
        status: "Not Started",
        teacher: appliedFilters.teachers.join(","),
        subject: appliedFilters.subjects.join(","),
      });

      if (response?.data?.liveClasses?.length > 0) {
        setUpcomingCount(response?.data?.count);
        if (isConcate) {
          setUpcomingClassData([
            ...upcomingClassData,
            ...response?.data?.liveClasses,
          ]);
        } else {
          setUpcomingClassData(response?.data?.liveClasses);
        }
      } else {
        setUpcomingClassData([]);
      }

      setNoOfUpcomingPages(Math.ceil(response?.data?.count / pageSize));
      setUpCommingLoader(false);
    } catch (error) {
      console.error("Error fetching upcoming classes:", error.message);
      setUpCommingLoader(false);
    }
  };

  // Get live classes
  const getLiveClasses = async ({
    pageNo,
    teacher,
    category,
    isConcate = false,
    noLoading = false,
    isPageSize = false,
    updatedPageSize = 10,
  }) => {
    try {
      if (!noLoading) {
        setLiveLoader(true);
      }

      const response = await getAllLiveClasses({
        id,
        pageNumber: pageNo - 1,
        pageSize: isPageSize ? updatedPageSize : pageSize,
        status: "Live",
        teacher: appliedFilters.teachers.join(","),
        subject: appliedFilters.subjects.join(","),
      });

      if (response?.data?.liveClasses?.length > 0) {
        if (isConcate) {
          setLiveClassData([...liveClassData, ...response?.data?.liveClasses]);
        } else {
          setLiveClassData(response?.data?.liveClasses);
        }

        if (stopFirstRefresh.current) {
          let updateUpcoming = [...upcomingClassData];
          response?.data?.liveClasses?.forEach((liveClass) => {
            updateUpcoming = updateUpcoming.filter(
              (upcoming) => upcoming?._id !== liveClass?._id,
            );
          });
          setUpcomingClassData(updateUpcoming);
        }
      } else {
        setLiveClassData([]);
      }

      setNoOfLivePages(Math.ceil(response?.data?.count / pageSize));
      setLiveLoader(false);
    } catch (error) {
      console.error("Error fetching live classes:", error.message);
      setLiveLoader(false);
    }
  };

  // Get completed live classes
  const getCompletedLiveClasses = async ({
    pageNo,
    teacher,
    category,
    isConcate = false,
    isPageSize = false,
    updatedPageSize = 10,
    noLoading = false,
  }) => {
    try {
      if (!noLoading) {
        setCompletedLoader(true);
      }

      const response = await getAllLiveClasses({
        id,
        pageNumber: pageNo - 1,
        pageSize: isPageSize ? updatedPageSize : pageSize,
        status: "Completed",
        teacher: appliedFilters.teachers.join(","),
        subject: appliedFilters.subjects.join(","),
      });

      if (response?.data?.liveClasses?.length > 0) {
        setCompletedCount(response?.data?.count);
        if (isConcate) {
          setCompletedClassData([
            ...completedClassData,
            ...response?.data?.liveClasses,
          ]);
        } else {
          setCompletedClassData(response?.data?.liveClasses);
        }
      } else {
        setCompletedClassData([]);
      }

      setNoOfCompletedPages(Math.ceil(response?.data?.count / pageSize));
      setCompletedLoader(false);
    } catch (error) {
      console.error("Error fetching completed classes:", error.message);
      setCompletedLoader(false);
    }
  };

  // Update recording flags for completed classes
  const updateRecordingFlagsForCompletedLiveClasses = async () => {
    try {
      const meetingIds = completedClassData
        .filter((completedLiveClass) => !completedLiveClass?.hasRecordingFile)
        .map((completedLiveClass) => completedLiveClass?.meetingID);

      if (meetingIds?.length) {
        await updateRecordingsForCompletedLiveClasses({
          meetingIds: meetingIds,
        });
      }
    } catch (error) {
      console.error(
        "Error Updating Completed Live Classes recordings:",
        error?.message,
      );
    }
  };

  // Start a live class
  const startLiveClass = async (
    liveclassid,
    meetingId,
    password,
    key = "start",
  ) => {
    setLoading(true);
    try {
      const formatData = { meetingID: meetingId, password: password };
      const payload = JSON.stringify(formatData);
      const response = await joinLiveClass(liveclassid, payload, key);

      if (response.data?.url) {
        openLinkInNewTab(response.data.url);
        resetFilters();
      } else {
        toast.error("Failed to join the live class");
      }
    } catch (error) {
      toast.error(error.message || "Failed to join the live class");
    } finally {
      setLoading(false);
    }
  };

  // End a live class
  const removeLiveClass = async (liveclassid, meetingId, password) => {
    setLoading(true);
    try {
      const formatData = {
        meetingID: meetingId,
        password: password,
      };
      const payload = JSON.stringify(formatData);
      const response = await endLiveClass(liveclassid, payload);

      if (response.error) {
        toast.error(response.error);
        return false;
      }

      if (response.ok && response.data) {
        toast.success("LiveClass ended successfully");
        resetFilters();
        return true;
      }

      toast.error("Failed to end the live class.");
      return false;
    } catch (error) {
      toast.error(error.message || "Failed to end the live class.");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Delete a live class
  const deleteLiveClassHandler = async (rowId) => {
    try {
      await deleteLiveClassroom(rowId);
      setLiveClassData((prev) => prev.filter((c) => c._id !== rowId));
      setUpcomingClassData((prev) => prev.filter((c) => c._id !== rowId));
      setCompletedClassData((prev) => prev.filter((c) => c._id !== rowId));
      toast.success("Live Class Deleted Successfully");
    } catch (error) {
      toast.error("Live Class Delete Failed");
      console.error("Error deleting Live Class:", error);
    }
  };

  // Suspend/resume a live class
  const suspendLiveClass = async (selectedLiveClass) => {
    try {
      setLoading(true);
      const payload = {
        isSuspended: !selectedLiveClass?.isSuspended,
      };
      const response = await updateLiveClass(
        selectedLiveClass?._id,
        JSON.stringify(payload),
      );

      if (response?.data?.liveClass) {
        toast.success(
          `Live class ${
            selectedLiveClass?.isSuspended ? "resumed" : "suspended"
          } successfully!`,
        );

        const updatedList = structuredClone(upcomingClassData);
        const ind = updatedList.findIndex(
          (event) => event?._id === selectedLiveClass?._id,
        );

        if (ind !== -1) {
          updatedList[ind].isSuspended = !updatedList[ind].isSuspended;
          setUpcomingClassData(updatedList);
        }

        setLoading(false);
        return true;
      } else {
        toast.error(
          response?.error?.message ||
            `Unable to ${
              selectedLiveClass?.isSuspended ? "resume" : "suspend"
            } the live class.`,
        );
        setLoading(false);
        return false;
      }
    } catch (err) {
      setLoading(false);
      toast.error(
        `Unable to ${
          selectedLiveClass?.isSuspended ? "resume" : "suspend"
        } the live class.`,
      );
      console.error("err", err);
      return false;
    }
  };

  // Reset filters and reload data
  const resetFilters = () => {
    setPageOfCompleted(1);
    setPageOfLive(1);
    setPageOfUpcoming(1);
    getUpomingLiveClasses({ pageNo: 1 });
    getLiveClasses({ pageNo: 1 });
    getCompletedLiveClasses({ pageNo: 1 });
    setAppliedFilters({
      status: [],
      teachers: [],
      subjects: [],
    });
  };

  // Apply filters
  const applyFilters = (data) => {
    let teacher = data["teachers"];
    let subjects = data["subjects"];
    let status = data["status"];

    setAppliedFilters({
      status: status,
      teachers: teacher,
    });
    setPageOfCompleted(1);
    setPageOfLive(1);
    setPageOfUpcoming(1);
    setAppliedFilters({
      status: status || [],
      teachers: teacher || [],
      subjects: subjects || [],
    });
    // getUpomingLiveClasses({ pageNo: 1, teacher: teacher, category: category });
    // getLiveClasses({
    //   pageNo: 1,
    //   teacher: teacher,
    //   category: category,
    // });
    // getCompletedLiveClasses({
    //   pageNo: 1,
    //   teacher: teacher,
    //   category: category,
    // });
  };

  // Load more pages
  const loadMoreLiveClasses = () => {
    getLiveClasses({
      pageNo: pageOfLive + 1,
      isConcate: true,
    });
    setPageOfLive(pageOfLive + 1);
  };

  const loadMoreUpcomingClasses = () => {
    getUpomingLiveClasses({
      pageNo: pageOfUpcoming + 1,
      isConcate: true,
    });
    setPageOfUpcoming(pageOfUpcoming + 1);
  };

  const loadMoreCompletedClasses = () => {
    getCompletedLiveClasses({
      pageNo: pageOfCompleted + 1,
      isConcate: true,
    });
    setPageOfCompleted(pageOfCompleted + 1);
  };

  // Initial data loading
  useEffect(() => {
    getUpomingLiveClasses({ pageNo: 1 });
    getLiveClasses({ pageNo: 1 });
    getCompletedLiveClasses({ pageNo: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters]);

  // Refresh mechanism
  useEffect(() => {
    if (stopFirstRefresh.current) {
      // getUpomingLiveClasses({
      //   pageNo: 1,
      //   isPageSize: true,
      //   updatedPageSize: (pageSize - 1) * pageOfUpcoming,
      //   noLoading: true,
      // });
      getLiveClasses({
        pageNo: 1,
        isPageSize: true,
        updatedPageSize: (pageSize - 1) * pageOfLive,
        noLoading: true,
      });
    } else {
      stopFirstRefresh.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshClasses]);

  // Refresh mechanism
  useEffect(() => {
    if (stopFirstCompletedRefresh.current) {
      getCompletedLiveClasses({
        pageNo: 1,
        isPageSize: true,
        updatedPageSize: (pageSize - 1) * pageOfCompleted,
        noLoading: true,
      });
    } else {
      stopFirstCompletedRefresh.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshCompletedClasses]);

  useEffect(() => {
    const refreshInterval = setInterval(() => {
      setRefreshClasses(!refreshClasses);
    }, 15000);
    //Clearing the interval
    return () => clearInterval(refreshInterval);
  }, [refreshClasses]);

  useEffect(() => {
    const refreshInterval = setInterval(() => {
      setRefreshCompletedClasses(!refreshCompletedClasses);
    }, 60000);
    //Clearing the interval
    return () => clearInterval(refreshInterval);
  }, [refreshCompletedClasses]);

  useEffect(() => {
    updateRecordingFlagsForCompletedLiveClasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshCompletedLiveClasses, completedClassData.length]);

  useEffect(() => {
    const refreshInterval = setInterval(() => {
      setRefreshCompletedLiveClasses(!refreshCompletedLiveClasses);
    }, 300000);
    //Clearing the interval
    return () => clearInterval(refreshInterval);
  }, [refreshCompletedLiveClasses, completedClassData.length]);

  return {
    // Data
    liveClassData,
    upcomingClassData,
    completedClassData,

    // Loading states
    loading,
    liveLoader,
    upComingLoader,
    completedLoader,

    // Pagination states
    noOfLivePages,
    pageOfLive,
    noOfUpcomingPages,
    pageOfUpcoming,
    noOfCompletedPages,
    pageOfCompleted,

    // Actions
    startLiveClass,
    removeLiveClass,
    deleteLiveClassHandler,
    suspendLiveClass,
    resetFilters,
    applyFilters,

    // Load more functions
    loadMoreLiveClasses,
    loadMoreUpcomingClasses,
    loadMoreCompletedClasses,
    appliedFilters,
    completedCount,
    upcomingCount,
    refreshCompletedOnes: () => {
      setRefreshCompletedClasses(!refreshCompletedClasses)
    }
  };
};

export default useLiveClassData;
